import { useEffect, useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";

import FormikInput from "@components/form/FormikInput";
import FormikCombobox from "@components/form/FormikCombobox";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import axiosClient from "@service/axiosClient";
import HeadlessButton from "@components/Button";
import LanguageToggle from "@components/constant_components/LanguageToggle";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";

export const buildValidationSchema = (t: (key: string) => string) =>
  Yup.object({
    first_name: Yup.string().required(t("validation.firstName")),
    second_first_name: Yup.string(),
    third_name: Yup.string().required(t("validation.lastName")),
    fourth_name: Yup.string(),
    email: Yup.string()
      .email(t("validation.emailInvalid"))
      .required(t("validation.email")),
    password: Yup.string()
      .min(8, t("validation.passwordMin"))
      .required(t("validation.passRequired")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("validation.passwordMatch"))
      .required(t("validation.confirmPassword")),
    role: Yup.string().required(t("validation.role")),
    roleGroup: Yup.string().required(t("validation.roleGroup")),
    plant: Yup.string().required(t("validation.plant")),
  });

const initialValues = {
  first_name: "",
  second_name: "",
  third_name: "",
  fourth_name: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: "",
  roleGroup: "",
  plant: "",
};

export const AdminRegistrationForm = () => {
  const navigate = useNavigate();
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [roleGroups, setRoleGroups] = useState<
    { value: string; label: string }[]
  >([]);
  const [plants, setPlants] = useState<{ value: string; label: string }[]>([]);
  const [loadingPlants, setLoadingPlants] = useState(false);
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const { t } = useTranslation();
  const validationSchema = buildValidationSchema(t);

  useEffect(() => {
    // Fetch role groups for the combobox
    const fetchRoleGroups = async () => {
      try {
        const response = await axiosClient.get("/role_group_api/role_groups");
        // Normalize to array (adjust keys to your API: results, data, clients, etc.)
        //   const items = Array.isArray(response.data)
        //     ? response.data
        //     : response.data?.results ??
        //       response.data?.clients ??
        //       response.data?.data ??
        //       []:
        const roleGroupOptions = Array.isArray(response.data)
          ? response.data.map((roleGroup: any) => ({
              value: roleGroup.name,
              label: roleGroup.id,
            }))
          : [];
        setRoleGroups(roleGroupOptions);
      } catch (error) {
        console.error("Error fetching role groups:", error);
      }
    };
    fetchRoleGroups();
  }, []);

  const fetchPlantsForRoleGroup = useCallback(async (roleGroupId: string) => {
    if (!roleGroupId) {
      setPlants([]);
      return;
    }
    try {
      setPlants([]);
      setLoadingPlants(true);
      const resp = await axiosClient.get(
        `/plant_api/plants/role_group/${roleGroupId}`
      );
      const options = Array.isArray(resp.data)
        ? resp.data.map((p: any) => ({
            value: p.name,
            label: String(p.id),
          }))
        : [];
      setPlants(options);
    } catch (e) {
      console.error("Error fetching plants", e);
    } finally {
      setLoadingPlants(false);
    }
  }, []);

  const fetchRolesForRoleGroup = useCallback(async (roleGroupId: string) => {
    if (!roleGroupId) {
      setRoles([]);
      return;
    }
    try {
      setRoles([]);
      setLoadingRoles(true);
      const resp = await axiosClient.get(
        `/role_api/roles/role_group/${roleGroupId}`
      );
      const options = Array.isArray(resp.data)
        ? resp.data.map((r: any) => ({
            value: r.name,
            label: String(r.id),
          }))
        : [];
      setRoles(options);
    } catch (e) {
      console.error("Error fetching roles", e);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const onSubmit = useCallback(
    async (
      values: any,
      { setSubmitting }: import("formik").FormikHelpers<any>
    ) => {
      try {
        // const { roleGroup, plant, role } = values;
        const roleGroupObj = roleGroups.find(
          (c) => c.value === values.roleGroup
        ) || {
          label: "",
        };
        const plantObj = plants.find((p) => p.value === values.plant) || {
          label: "",
        };
        const roleObj = roles.find((r) => r.value === values.role) || {
          label: "",
        };
        const data = {
          email: values.email,
          first_name: values.first_name,
          third_name: values.third_name,
          password: values.password,
          fourth_name: values.fourth_name,
          second_name: values.second_name,
          role_group_id: roleGroupObj.label,
          plant_id: plantObj.label,
          role_id: roleObj.label,
        };
        await axiosClient.post(`/register/admin`, data);
        triggerSnackbar(`Registration executed successfully.`, "success");
        navigate("/login/admin");
      } catch (err: any) {
        triggerSnackbar(
          `Registration failed: ${err.response.data.error}.`,
          "error"
        );
        console.error("Registration failed:", err.response.data.error);
      } finally {
        setSubmitting(false);
      }
    },
    [roleGroups, plants, roles, triggerSnackbar, navigate]
  );

  return (
    <>
      {/* Language toggle at top-right corner */}
      <div className="tw-absolute tw-top-4 tw-right-4 tw-z-50">
        <LanguageToggle />
      </div>
      <div className="tw-form-container">
        <div className="tw-form-overlay" />
        <div className="tw-form-wrapper">
          <h1 className="register-title tw-h-10">
            {t("AdminRegistration.registerAdmin")}
          </h1>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, values }) => (
              <Form className="tw-space-y-5">
                <Field
                  label={t("AdminRegistration.firstName")}
                  name="first_name"
                  as={FormikInput}
                  error={
                    touched.first_name && errors.first_name
                      ? errors.first_name
                      : undefined
                  }
                />
                <Field
                  label={t("AdminRegistration.secondName")}
                  name="second_name"
                  as={FormikInput}
                />
                <Field
                  label={t("AdminRegistration.lastName")}
                  name="third_name"
                  as={FormikInput}
                  error={
                    touched.third_name && errors.third_name
                      ? errors.third_name
                      : undefined
                  }
                />
                <Field
                  label={t("AdminRegistration.secondLastName")}
                  name="fourth_name"
                  as={FormikInput}
                  error={
                    touched.fourth_name && errors.fourth_name
                      ? errors.fourth_name
                      : undefined
                  }
                />
                <Field
                  label={t("AdminRegistration.email")}
                  name="email"
                  type="email"
                  as={FormikInput}
                  error={
                    touched.email && errors.email ? errors.email : undefined
                  }
                />
                <Field name="roleGroup">
                  {({
                    field,
                    form,
                    meta,
                  }: {
                    field: any;
                    form: import("formik").FormikProps<typeof initialValues>;
                    meta: any;
                  }) => (
                    <FormikCombobox
                      label={t("AdminRegistration.roleGroup")}
                      name={field.name}
                      options={roleGroups}
                      onOptionChange={async (
                        opt: { value: string; label: string } | null
                      ) => {
                        // Store the option value (name) in the field to match
                        // the plant/role convention and the value lookups below;
                        // the label (id) is only needed for the fetch calls.
                        const roleGroupName = opt?.value || "";
                        const roleGroupId = opt?.label || "";
                        form.setFieldValue("roleGroup", roleGroupName);
                        // clear plant whenever role group changes
                        form.setFieldValue("plant", "");
                        form.setFieldValue("role", "");
                        setPlants([]);
                        setRoles([]);
                        if (roleGroupId) {
                          await fetchPlantsForRoleGroup(roleGroupId);
                          await fetchRolesForRoleGroup(roleGroupId);
                        }
                      }}
                      value={
                        roleGroups.find((c) => c.value === field.value) || null
                      }
                      placeholder={t("AdminRegistration.selectRoleGroup", {
                        defaultValue: "Select a role group",
                      })}
                      error={
                        meta.touched && meta.error ? meta.error : undefined
                      }
                    />
                  )}
                </Field>

                {values.roleGroup && (
                  <>
                    <Field name="plant">
                      {({
                        field,
                        form,
                        meta,
                      }: {
                        field: import("formik").FieldProps["field"];
                        form: import("formik").FormikProps<
                          typeof initialValues
                        >;
                        meta: any;
                      }) => {
                        // If no plants or current value not valid -> clear it
                        const valid = plants.some(
                          (p) => p.value === field.value
                        );
                        if ((!plants.length || !valid) && field.value) {
                          form.setFieldValue("plant", "");
                        }
                        const selected =
                          plants.find((p) => p.value === field.value) || null;
                        return (
                          <FormikCombobox
                            label={t("AdminRegistration.plant")}
                            name={field.name}
                            options={plants}
                            value={selected}
                            placeholder={
                              loadingPlants
                                ? t("AdminRegistration.loadingPlants", {
                                    defaultValue: "Loading plants...",
                                  })
                                : plants.length
                                ? t("AdminRegistration.selectPlant", {
                                    defaultValue: "Select a plant",
                                  })
                                : t("AdminRegistration.noPlants", {
                                    defaultValue: "No plants available",
                                  })
                            }
                            error={
                              meta.touched && meta.error
                                ? meta.error
                                : undefined
                            }
                          />
                        );
                      }}
                    </Field>
                    <Field name="role">
                      {({
                        field,
                        form,
                        meta,
                      }: {
                        field: import("formik").FieldProps["field"];
                        form: import("formik").FormikProps<
                          typeof initialValues
                        >;
                        meta: any;
                      }) => {
                        // If no roles or current value not valid -> clear it
                        const valid = roles.some(
                          (r) => r.value === field.value
                        );
                        if ((!roles.length || !valid) && field.value) {
                          form.setFieldValue("role", "");
                        }
                        const selected =
                          roles.find((r) => r.value === field.value) || null;
                        return (
                          <FormikCombobox
                            label={t("AdminRegistration.role")}
                            name={field.name}
                            options={roles}
                            value={selected}
                            placeholder={
                              loadingRoles
                                ? t("AdminRegistration.loadingRoles", {
                                    defaultValue: "Loading roles...",
                                  })
                                : roles.length
                                ? t("AdminRegistration.selectRole", {
                                    defaultValue: "Select a role",
                                  })
                                : t("AdminRegistration.noRoles", {
                                    defaultValue: "No roles available",
                                  })
                            }
                            error={
                              meta.touched && meta.error
                                ? meta.error
                                : undefined
                            }
                          />
                        );
                      }}
                    </Field>
                  </>
                )}
                <Field
                  label={t("AdminRegistration.password")}
                  name="password"
                  type="password"
                  as={FormikInput}
                  error={
                    touched.password && errors.password
                      ? errors.password
                      : undefined
                  }
                />
                <Field
                  label={t("AdminRegistration.confirmPassword")}
                  name="confirmPassword"
                  type="password"
                  as={FormikInput}
                  error={
                    touched.confirmPassword && errors.confirmPassword
                      ? errors.confirmPassword
                      : undefined
                  }
                />
                <HeadlessButton
                  className="tw-w-full tw-bg-primary-light tw-text-white tw-py-2 tw-rounded-md tw-font-semibold"
                  theme="Primary-Blue"
                  type="submit"
                  disabled={false}
                  data-testid="submit-btn"
                  // If you have isLoading state, add it to the component's state and Formik render props
                  // disabled={isLoading}
                >
                  {/* If you have isLoading, use it here */}
                  {/* {isLoading ? (
                    <div className="tw-flex tw-justify-center tw-align-middle tw-gap-2">
                      {t("CreateNewContractor.processing")}
                      <Spinner className="tw-mt-0.5" />
                    </div>
                  ) : ( */}
                  {t("validation.submit")}
                  {/* )} */}
                </HeadlessButton>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};
